"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

export const NotificationsCard = () => {
  const { t } = useTranslation(["workspaces", "common"]);

  // Mock notifications data - REMOVED, now empty for demonstration
  const notifications: any[] = []; // This will now be an empty array

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) {
      return t("workspaces:dashboard.notifications.daysAgo", { count: days });
    }
    if (hours > 0) {
      return t("workspaces:dashboard.notifications.hoursAgo", { count: hours });
    }

    return t("workspaces:dashboard.notifications.justNow");
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-foreground/50 h-fit rounded-2xl border-none p-4 pe-2 backdrop-blur-xl sm:p-6 sm:pe-2"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.5 }}
    >
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <Icon
            className="text-accent-foreground"
            height="1.25rem"
            icon="mdi:bell-outline"
            width="1.25rem"
          />
          <h3 className="text-accent-foreground text-base font-semibold sm:text-lg">
            {t("workspaces:dashboard.notifications.title")}
          </h3>
        </div>
        {unreadCount > 0 && (
          <Badge className="border-red-500/30 bg-red-500/20 text-xs text-red-400" variant="outline">
            {unreadCount}
          </Badge>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="flex h-60 flex-col items-center justify-center text-center sm:h-80">
          <Icon
            className="text-muted-foreground mb-3 sm:mb-4"
            height="2.5rem"
            icon="mdi:bell-off-outline"
            width="2.5rem"
          />
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("workspaces:dashboard.notifications.noNotifications")}
          </p>
        </div>
      ) : (
        <ScrollArea className="h-60 sm:h-80">
          <div className="space-y-2 pe-2 sm:space-y-3 sm:pe-4">
            {notifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                animate={{ opacity: 1, scale: 1 }}
                className={`group bg-muted/20 hover:bg-muted/30 relative cursor-pointer overflow-hidden rounded-lg p-3 transition-all duration-300 hover:scale-[1.01] sm:rounded-xl sm:p-4 sm:hover:scale-[1.02] ${
                  !notification.read ? "bg-blue-500/5" : ""
                }`}
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                {/* Decorative circle */}
                <div
                  className={`absolute -top-4 -right-4 h-12 w-12 rounded-full ${
                    notification.type === "lesson_reminder"
                      ? "bg-gradient-to-br from-blue-400/20 to-purple-600/20"
                      : notification.type === "homework_due"
                        ? "bg-gradient-to-br from-orange-400/20 to-red-500/20"
                        : notification.type === "message"
                          ? "bg-gradient-to-br from-green-400/20 to-blue-500/20"
                          : "bg-gradient-to-br from-purple-400/20 to-pink-600/20"
                  }`}
                />

                <div className="relative flex items-start gap-2 sm:gap-3">
                  <div className="bg-muted/30 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md sm:h-8 sm:w-8 sm:rounded-lg">
                    <Icon
                      className={notification.color}
                      height="0.875rem"
                      icon={notification.icon}
                      width="0.875rem"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex items-center justify-between sm:mb-1">
                      <h4 className="text-accent-foreground text-xs font-medium sm:text-sm">
                        {notification.title}
                      </h4>
                      {!notification.read && (
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-500 sm:h-2 sm:w-2" />
                      )}
                    </div>
                    <p className="text-muted-foreground mb-1 text-[0.65rem] leading-relaxed sm:mb-2 sm:text-xs">
                      {notification.message}
                    </p>
                    <span className="text-muted-foreground text-[0.65rem] sm:text-xs">
                      {getTimeAgo(notification.timestamp)}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ScrollArea>
      )}

      <div className="border-border/50 mt-3 border-t pt-3 sm:mt-4 sm:pt-4">
        <Button
          className="w-full text-xs sm:text-sm"
          disabled={notifications.length === 0}
          size="sm"
          variant="foreground"
        >
          <Icon className="mr-2" height="0.875rem" icon="mdi:bell-check" width="0.875rem" />
          {t("workspaces:dashboard.notifications.markAllRead")}
        </Button>
      </div>
    </motion.div>
  );
};
